import { Edit } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  TextField,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProcessStep } from '../../common/enums/enums';
import { GenCurrency } from '../GenDataFormat';
import GenDataText from '../GenDataText';
import GenPaymentStatus from '../GenPaymentStatus';
import GenSkeleton from '../GenSkeleton';
import GenTables from '../GenTables';
import SectionTitle from '../SectionTitle';
import { PaymentServiceDropdown } from '../WorkOrderForVehicle/PaymentServiceDropdown';

const GenPaymentStatusWrapper = ({ text }) => {
  return <GenPaymentStatus amount={text[0]} unpaid={text[0] - text[1]} />;
};

const UnpaidValue = ({ text }) => {
  return <GenCurrency value={text[0] - text[1]} />;
};

const EditComponent = ({
  changeEditState,
  editState,
  currentValue,
  currentPaid,
  serviceId,
  editServiceFunction,
  originalPrice,
  vendorOrderId,
  isAdminRole = false,
}) => {
  const [editedValue, setEditedValue] = useState(currentValue);

  let temporaryValue = editedValue;

  const validateAndUpdatePrice = (value, isValidValue) => {
    const sendValue =
      isValidValue(value) && !isNaN(value) ? value : currentValue;

    if (currentValue !== sendValue) {
      editServiceFunction({
        vendorOrderId,
        serviceId,
        value: sendValue,
        field: 'price',
      });
    }

    return sendValue;
  };

  const handlePartiallyPaid = value => {
    if (isAdminRole) {
      return validateAndUpdatePrice(value, () => true);
    } else {
      return validateAndUpdatePrice(
        value,
        val => val < currentValue && val >= currentPaid
      );
    }
  };

  const handleNotPaid = value => {
    if (isAdminRole) {
      return validateAndUpdatePrice(value, () => true);
    } else {
      return validateAndUpdatePrice(
        value,
        val => val <= originalPrice && val >= 0
      );
    }
  };

  const handleMouseLeave = () => {
    if (currentPaid !== 0) {
      temporaryValue = handlePartiallyPaid(temporaryValue);
    } else {
      temporaryValue = handleNotPaid(temporaryValue);
    }
    setEditedValue(temporaryValue);
    changeEditState();
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const ref = useRef();
  useEffect(() => {
    if (editState) {
      ref.current.focus();
    }
  }, [editState]);

  if (editState) {
    return (
      <GenCurrency
        value={editedValue}
        displayType="input"
        onValueChange={values => {
          temporaryValue = values.floatValue;
        }}
        onBlur={handleMouseLeave}
        onKeyDown={e => handleKeyPress(e)}
        customInput={params => {
          return (
            <TextField
              {...params}
              fullWidth
              autoComplete="off"
              variant="outlined"
              size="small"
              inputProps={{
                'data-testid': 'editPriceInputField',
                ref,
              }}
            />
          );
        }}
      />
    );
  } else {
    return <GenCurrency value={editedValue} aria-label={'service value'} />;
  }
};

const WrapperCurrency = ({
  text,
  isEditable,
  apiIsLoading,
  editServiceFunction,
  vendorOrderId,
  isBilled,
  isAdminRole = false,
}) => {
  const originalPrice = text[0];
  const price = text[1];
  const totalPaid = text[2];
  const serviceId = text[3];
  const [edit, setEdit] = useState(false);

  const handleEditButtonClick = useCallback(() => {
    setEdit(!edit);
  }, [edit]);

  const isServicePriceEditable = (
    editable,
    pRice,
    totalP,
    billed,
    originalP
  ) => {
    if (isAdminRole && !billed) return true;
    if (editable && !billed)
      return pRice > totalP || (pRice === 0 && originalP > 0);
    return false;
  };

  if (
    isServicePriceEditable(
      isEditable,
      price,
      totalPaid,
      isBilled,
      originalPrice
    )
  ) {
    return (
      <Grid container alignItems="center">
        <Grid item>
          <EditComponent
            changeEditState={handleEditButtonClick}
            editState={edit}
            currentValue={price}
            currentPaid={totalPaid}
            serviceId={serviceId}
            apiIsLoading={apiIsLoading}
            editServiceFunction={editServiceFunction}
            originalPrice={originalPrice}
            vendorOrderId={vendorOrderId}
            isAdminRole={isAdminRole}
          />
        </Grid>
        <Grid item>
          {apiIsLoading?.loading && apiIsLoading?.service === serviceId ? (
            <Box p={1} data-testid="loadingSpinner">
              <CircularProgress
                sx={{ color: theme => theme.palette.text.secondary }}
                size={20}
                thickness={6}
              />
            </Box>
          ) : (
            <IconButton
              onClick={handleEditButtonClick}
              data-testid="editServicePriceButton"
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
        </Grid>
      </Grid>
    );
  } else {
    return <GenCurrency value={price} />;
  }
};
const AmountWrapper = ({ text }) => <GenCurrency value={text} />;

const PaymentSummaryLoading = () => (
  <Box
    data-testid="skeleton"
    sx={{
      bgcolor: 'background.paper',
      p: { xs: 2, lg: 4 },
      borderTop: '1px solid',
      borderColor: theme => theme.palette.divider,
    }}
  >
    <Grid container flexDirection="column" spacing={2}>
      <Grid item>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Skeleton variant="text" width={120} />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <GenSkeleton sx={{ height: '290px' }} />
      </Grid>
    </Grid>
  </Box>
);

const PaymentSummaryData = ({
  data,
  isEditable,
  enablePaymentEditableFields,
  services = [],
  apiIsLoading,
  editServiceFunction,
  isAdminRole = false,
}) => {
  const { t } = useTranslation('workOrdersTable');
  const columns = [
    {
      id: 'name',
      label: t('detail.paymentColumnLabels.nameLabel'),
      labelComponent: (
        <GenDataText textVariant="body1" textColor="textPrimary" />
      ),
      component: (
        <PaymentServiceDropdown
          enablePaymentEditableFields={enablePaymentEditableFields}
          services={services}
          apiIsLoading={apiIsLoading}
          editServiceFunction={editServiceFunction}
          vendorOrderId={data.vendorOrderId}
        />
      ),
      dataSource: [['name', 'quantity', 'serviceId']],
      align: 'inherit',
    },
    {
      id: 'invoiceNumber',
      label: t('detail.paymentColumnLabels.invoiceNumber'),
      dataSource: ['invoiceNumber'],
      defaultValue: '-',
      align: 'left',
      sx: { display: { xs: 'none', sm: 'none', lg: 'table-cell' } },
    },
    {
      id: 'paymentStatus',
      label: t('detail.paymentColumnLabels.paymentStatus'),
      dataSource: [['amount', 'totalPaid']],
      component: <GenPaymentStatusWrapper />,
      align: 'left',
      sx: { display: { xs: 'none', sm: 'table-cell' } },
    },
    {
      id: 'price',
      label: t('detail.paymentColumnLabels.price'),
      component: (
        <WrapperCurrency
          isEditable={isEditable}
          apiIsLoading={apiIsLoading}
          editServiceFunction={editServiceFunction}
          vendorOrderId={data.vendorOrderId}
          isBilled={data.isBilled}
          isAdminRole={isAdminRole}
        />
      ),
      dataSource: [['originalPrice', 'price', 'totalPaid', 'serviceId']],
      align: 'left',
      sx: { display: { xs: 'none', lg: 'table-cell' } },
    },
    {
      id: 'amount',
      label: t('detail.paymentColumnLabels.amount'),
      component: <AmountWrapper />,
      dataSource: ['amount'],
      align: 'left',
      sx: { display: { xs: 'none', sm: 'table-cell' } },
    },
    {
      id: 'unpaid',
      label: t('detail.paymentColumnLabels.unpaid'),
      component: <UnpaidValue />,
      dataSource: [['amount', 'totalPaid']],
      align: 'left',
    },
    {
      id: 'serviceId',
      label: t('detail.paymentColumnLabels.serviceId'),
      dataSource: ['serviceId'],
      align: 'left',
      sx: { display: { xs: 'none', sm: 'none', lg: 'table-cell' } },
    },
  ];

  return (
    data?.step === ProcessStep.DONE && (
      <Box
        sx={{
          bgcolor: 'background.paper',
          p: { xs: 2, lg: 4 },
          borderTop: '1px solid',
          borderColor: theme => theme.palette.divider,
        }}
      >
        <Grid container flexDirection="column" spacing={0.5}>
          <Grid item>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item>
                <SectionTitle
                  title={t('card.visibleContent.paymentSummary')}
                  subsection={true}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <GenTables
              tableHeadSx={{
                backgroundColor: theme => theme.palette.grey[50],
              }}
              data={data.services}
              columns={columns}
            />
          </Grid>
        </Grid>
      </Box>
    )
  );
};

const WOrderPaymentSummary = ({
  data,
  isLoading,
  isEditable,
  apiIsLoading,
  enablePaymentEditableFields = false,
  services = [],
  editServiceFunction,
  isAdminRole = false,
}) => {
  return isLoading ? (
    <PaymentSummaryLoading />
  ) : (
    <PaymentSummaryData
      data={data}
      isEditable={isEditable}
      apiIsLoading={apiIsLoading}
      enablePaymentEditableFields={enablePaymentEditableFields}
      services={services}
      editServiceFunction={editServiceFunction}
      isAdminRole={isAdminRole}
    />
  );
};

export default WOrderPaymentSummary;
